import { useEffect, useMemo, useState } from 'react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
} from 'recharts';
import RecoveryLayout from '../../components/RecoveryLayout';
import PageLoader from '../../components/PageLoader';
import axiosInstance from '../../utils/axiosInstance';

/* ── helpers ─────────────────────────────────────────────── */
const fmt = (v) =>
  new Intl.NumberFormat('en-US', { maximumFractionDigits: 0 }).format(v ?? 0);

const fmtShort = (v) => {
  const n = parseFloat(v) || 0;
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000)     return `${(n / 1_000).toFixed(1)}K`;
  return fmt(n);
};

// "2025-03-15" → "Mar 15"
const shortDate = (iso) => {
  if (!iso) return '';
  const d = new Date(iso + 'T00:00:00');
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
};

const STATUS_COLORS = {
  pending:     { bg: '#fee2e2', color: '#991b1b' },
  paid:        { bg: '#dcfce7', color: '#166534' },
  change:      { bg: '#fef3c7', color: '#92400e' },
  informed:    { bg: '#e0f2fe', color: '#0c4a6e' },
  confirmed:   { bg: '#ede9fe', color: '#5b21b6' },
  'No Answer': { bg: '#e5e7eb', color: '#374151' },
};
const STATUS_ORDER = ['pending', 'paid', 'change', 'informed', 'confirmed', 'No Answer'];

// Fill every day of the current month; days with no paid data get amount=null
const buildMonthGrid = (dailyRows) => {
  const now   = new Date();
  const year  = now.getFullYear();
  const month = now.getMonth();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const map   = {};
  dailyRows.forEach((r) => { if (r.date) map[r.date] = r.amount; });
  const grid  = [];
  for (let day = 1; day <= daysInMonth; day++) {
    const iso = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    grid.push({ date: iso, label: String(day), amount: map[iso] ?? null });
  }
  return grid;
};

// Multi-series version: each row may have paidAmount + confirmedAmount
const buildMonthGridMulti = (dailyRows, fields) => {
  const now   = new Date();
  const year  = now.getFullYear();
  const month = now.getMonth();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const map   = {};
  dailyRows.forEach((r) => { if (r.date) map[r.date] = r; });
  const grid  = [];
  for (let day = 1; day <= daysInMonth; day++) {
    const iso = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    const entry = { date: iso, label: String(day) };
    fields.forEach((f) => { entry[f] = map[iso]?.[f] ?? null; });
    grid.push(entry);
  }
  return grid;
};

/* ── sub-components ─────────────────────────────────────── */
function KpiCard({ label, value, sub, color = '#dc2626' }) {
  return (
    <div
      style={{
        background: '#fff',
        borderRadius: 12,
        padding: '20px 24px',
        boxShadow: '0 1px 3px rgba(0,0,0,0.07)',
        borderLeft: `4px solid ${color}`,
      }}
    >
      <p style={{ fontSize: 13, color: '#64748b', marginBottom: 6 }}>{label}</p>
      <p style={{ fontSize: 26, fontWeight: 700, color: '#0f172a' }}>{value}</p>
      {sub && <p style={{ fontSize: 12, color: '#94a3b8', marginTop: 4 }}>{sub}</p>}
    </div>
  );
}

function ChartTooltip({ active, payload, label }) {
  if (!active || !payload?.length || payload[0].value === null) return null;
  return (
    <div style={{
      background: '#fff', border: '1px solid #e2e8f0',
      borderRadius: 8, padding: '8px 14px', fontSize: 13,
    }}>
      <p style={{ color: '#64748b', marginBottom: 4 }}>{label}</p>
      <p style={{ color: '#16a34a', fontWeight: 700 }}>Rs {fmt(payload[0].value)}</p>
    </div>
  );
}

/* ── main component ─────────────────────────────────────── */
export default function Dashboard({ pageTitle, section }) {
  const [kpi, setKpi]                           = useState(null);
  const [statusCounts, setStatusCounts]         = useState({});
  const [reportMonth, setReportMonth]           = useState('');
  const [dailyPaid, setDailyPaid]               = useState([]);
  const [dailyConfirmed, setDailyConfirmed]     = useState([]);
  const [recentArrears, setRecentArrears]       = useState([]);
  const [recentPayments, setRecentPayments]     = useState([]);
  const [loading, setLoading]                   = useState(true);
  const [error, setError]                       = useState(null);
  const [extraModal, setExtraModal]             = useState({ open: false, loading: false, rows: [], month: '' });

  const openExtraDueModal = async () => {
    setExtraModal({ open: true, loading: true, rows: [], month: reportMonth });
    try {
      const resp = await axiosInstance.get('/admin/recovery-dues/extra-due-records', {
        params: { month: reportMonth },
      });
      setExtraModal((prev) => ({
        ...prev,
        loading: false,
        rows: resp.data.rows ?? [],
        month: resp.data.month ?? reportMonth,
      }));
    } catch {
      setExtraModal((prev) => ({ ...prev, loading: false }));
    }
  };

  useEffect(() => {
    let cancelled = false;

    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);

        const [summaryResp, tablesResp] = await Promise.allSettled([
          axiosInstance.get('/admin/recovery-dues/dashboard-summary'),
          axiosInstance.get('/admin/recovery-dashboard'),
        ]);

        if (cancelled) return;

        if (summaryResp.status === 'fulfilled' && summaryResp.value?.data) {
          const d = summaryResp.value.data;
          setKpi(d.kpi ?? null);
          setStatusCounts(d.statusCounts ?? {});
          setReportMonth(d.reportMonth ?? '');
          setDailyPaid(buildMonthGrid(d.dailyPaid ?? []));
          setDailyConfirmed(buildMonthGrid(d.dailyConfirmed ?? []));
        }

        if (tablesResp.status === 'fulfilled' && tablesResp.value?.data) {
          const d = tablesResp.value.data;
          setRecentArrears(d.recentArrears ?? []);
          setRecentPayments(d.recentPayments ?? []);
        }
      } catch {
        if (!cancelled) setError('Failed to load recovery dashboard data.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchData();
    return () => { cancelled = true; };
  }, []);

  const collectionPct = useMemo(() => {
    if (!kpi?.totalDueAmount) return 0;
    return Math.min(100, ((kpi.totalPaidAmount / kpi.totalDueAmount) * 100));
  }, [kpi]);

  const monthLabel = useMemo(() => {
    if (!reportMonth) return '';
    const d = new Date(reportMonth + '-01');
    return d.toLocaleString('default', { month: 'long', year: 'numeric' });
  }, [reportMonth]);

  if (loading) return (
    <RecoveryLayout title={pageTitle || 'Dashboard'} currentSection={section}>
      <PageLoader />
    </RecoveryLayout>
  );

  return (
    <RecoveryLayout title={pageTitle || 'Dashboard'} currentSection={section}>
      {error && (
        <div style={{ marginBottom: 16, padding: '12px 16px', background: '#fee2e2', borderRadius: 8, color: '#991b1b', fontSize: 14 }}>
          {error}
        </div>
      )}

      {/* ── KPI cards ─────────────────────────────────────── */}
      <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', marginBottom: 12, flexWrap: 'wrap', gap: 6 }}>
        <span style={{ fontSize: 13, fontWeight: 700, color: '#0f172a' }}>Monthly Summary</span>
        {monthLabel && (
          <span style={{ fontSize: 12, color: '#64748b', fontWeight: 500 }}>
            {monthLabel}
            {kpi?.totalStudents ? (
              <span style={{ marginLeft: 8, background: '#f1f5f9', borderRadius: 6, padding: '2px 8px', fontSize: 11, fontWeight: 700, color: '#475569' }}>
                {kpi.totalStudents} students
              </span>
            ) : null}
          </span>
        )}
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(210px, 1fr))', gap: 16, marginBottom: 16 }}>
        <KpiCard
          label="Total Due Amount"
          value={`Rs ${fmtShort(kpi?.totalDueAmount)}`}
          sub={`Rs ${fmt(kpi?.totalDueAmount)}`}
          color="#dc2626"
        />
        <KpiCard
          label="Paid Amount"
          value={`Rs ${fmtShort(kpi?.totalPaidAmount)}`}
          sub={`Rs ${fmt(kpi?.totalPaidAmount)}`}
          color="#16a34a"
        />
        <KpiCard
          label="Confirmed Amount"
          value={`Rs ${fmtShort(kpi?.totalConfirmedAmount)}`}
          sub={`Rs ${fmt(kpi?.totalConfirmedAmount)}`}
          color="#f59e0b"
        />
        <KpiCard
          label="Today's Confirmed"
          value={`Rs ${fmtShort(kpi?.todayConfirmedAmount)}`}
          sub={`Rs ${fmt(kpi?.todayConfirmedAmount)}`}
          color="#0ea5e9"
        />
        <KpiCard
          label="Remaining Due"
          value={`Rs ${fmtShort((kpi?.totalDueAmount ?? 0) - (kpi?.totalPaidAmount ?? 0))}`}
          sub={`Rs ${fmt((kpi?.totalDueAmount ?? 0) - (kpi?.totalPaidAmount ?? 0))}`}
          color="#7c3aed"
        />
      </div>

      {/* ── Actual vs Extra Due Breakdown ─────────────────── */}
      {(kpi?.extraStudents > 0 || kpi?.actualStudents > 0) && (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 16 }}>
          {/* Actual Due card */}
          {(() => {
            const pct = kpi?.actualDueAmount > 0 ? Math.min(100, (kpi.actualPaidAmount / kpi.actualDueAmount) * 100) : 0;
            const barColor = pct >= 75 ? '#16a34a' : pct >= 40 ? '#2563eb' : '#dc2626';
            return (
              <div style={{ background: '#fff', borderRadius: 12, padding: '14px 18px', boxShadow: '0 1px 3px rgba(0,0,0,0.07)', borderLeft: '4px solid #2563eb' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <div>
                    <p style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: '#64748b', margin: '0 0 6px' }}>Actual Due</p>
                    <p style={{ fontSize: 20, fontWeight: 700, color: '#1e40af', margin: '0 0 2px', lineHeight: 1 }}>Rs {fmtShort(kpi?.actualDueAmount)}</p>
                    <p style={{ fontSize: 11, color: '#93c5fd', margin: 0 }}>Rs {fmt(kpi?.actualDueAmount)}</p>
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, background: '#dbeafe', borderRadius: 20, padding: '5px 12px' }}>
                      <span style={{ fontSize: 15, fontWeight: 700, color: '#1d4ed8' }}>{kpi?.actualStudents ?? 0}</span>
                      <span style={{ fontSize: 11, color: '#3b82f6', fontWeight: 500 }}>students</span>
                    </span>
                    <p style={{ fontSize: 10, color: '#93c5fd', margin: '4px 0 0', textAlign: 'center' }}>with contact</p>
                  </div>
                </div>
                <div style={{ marginTop: 10 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 4 }}>
                    <span style={{ fontSize: 11, color: '#64748b' }}>Rs {fmtShort(kpi?.actualPaidAmount)} collected</span>
                    <span style={{ fontSize: 12, fontWeight: 700, color: barColor }}>{pct.toFixed(1)}%</span>
                  </div>
                  <div style={{ height: 6, borderRadius: 99, background: '#dbeafe', overflow: 'hidden' }}>
                    <div style={{ height: '100%', width: `${pct}%`, borderRadius: 99, background: barColor, transition: 'width 0.4s ease' }} />
                  </div>
                  <div style={{ display: 'flex', gap: 10, marginTop: 6, flexWrap: 'wrap', alignItems: 'center' }}>
                    {((kpi?.actualDueAmount ?? 0) - (kpi?.actualPaidAmount ?? 0)) > 0 && (
                      <span style={{ fontSize: 11, color: '#dc2626' }}>
                        Rs {fmtShort((kpi.actualDueAmount) - (kpi.actualPaidAmount))} remaining
                      </span>
                    )}
                    {(kpi?.actualConfirmedAmount > 0) && (
                      <span style={{ fontSize: 11, color: '#7c3aed' }}>
                        Rs {fmtShort(kpi.actualConfirmedAmount)} confirmed
                      </span>
                    )}
                    {(kpi?.actualPaidStudents > 0) && (
                      <span style={{ fontSize: 11, color: '#16a34a', fontWeight: 600 }}>
                        {kpi.actualPaidStudents} paid
                      </span>
                    )}
                    {((kpi?.actualStudents ?? 0) - (kpi?.actualPaidStudents ?? 0)) > 0 && (
                      <span style={{ fontSize: 11, color: '#dc2626', fontWeight: 600 }}>
                        {(kpi.actualStudents) - (kpi.actualPaidStudents)} remaining
                      </span>
                    )}
                  </div>
                </div>
              </div>
            );
          })()}
          {/* Extra Due card */}
          {(() => {
            const pct = kpi?.extraStudents > 0 ? Math.min(100, (kpi.extraPaidStudents / kpi.extraStudents) * 100) : 0;
            const barColor = pct >= 75 ? '#16a34a' : pct >= 40 ? '#d97706' : '#dc2626';
            return (
              <div
                onDoubleClick={openExtraDueModal}
                title="Double-click to view extra due records"
                style={{ background: '#fff', borderRadius: 12, padding: '14px 18px', boxShadow: '0 1px 3px rgba(0,0,0,0.07)', borderLeft: '4px solid #d97706', cursor: 'pointer', userSelect: 'none' }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <div>
                    <p style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: '#64748b', margin: '0 0 6px' }}>Extra Due</p>
                    <p style={{ fontSize: 20, fontWeight: 700, color: '#b45309', margin: '0 0 2px', lineHeight: 1 }}>Rs {fmtShort(kpi?.extraPaidAmount)}</p>
                    <p style={{ fontSize: 11, color: '#94a3b8', margin: 0 }}>Rs {fmt(kpi?.extraPaidAmount)} collected</p>
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, background: '#fef3c7', borderRadius: 20, padding: '5px 12px' }}>
                      <span style={{ fontSize: 15, fontWeight: 700, color: '#d97706' }}>{kpi?.extraStudents ?? 0}</span>
                      <span style={{ fontSize: 11, color: '#f59e0b', fontWeight: 500 }}>students</span>
                    </span>
                    <p style={{ fontSize: 10, color: '#fbbf24', margin: '4px 0 0', textAlign: 'center' }}>no contact</p>
                  </div>
                </div>
                <div style={{ marginTop: 10 }}>
                  <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'baseline', marginBottom: 4 }}>
                    <span style={{ fontSize: 12, fontWeight: 700, color: barColor }}>{pct.toFixed(1)}%</span>
                  </div>
                  <div style={{ height: 6, borderRadius: 99, background: '#fef3c7', overflow: 'hidden' }}>
                    <div style={{ height: '100%', width: `${pct}%`, borderRadius: 99, background: barColor, transition: 'width 0.4s ease' }} />
                  </div>                  {(kpi?.extraConfirmedAmount > 0) && (
                    <p style={{ margin: '6px 0 0', fontSize: 11, color: '#7c3aed' }}>
                      Rs {fmtShort(kpi.extraConfirmedAmount)} confirmed due
                    </p>
                  )}                </div>
              </div>
            );
          })()}
        </div>
      )}

      {/* ── Collection Progress ───────────────────────────── */}
      {(() => {
        const pct       = collectionPct;
        const remaining = (kpi?.totalDueAmount ?? 0) - (kpi?.totalPaidAmount ?? 0);
        const barColor  = pct >= 75 ? '#16a34a' : pct >= 40 ? '#f59e0b' : '#dc2626';
        return (
          <div style={{ background: '#fff', borderRadius: 12, padding: '18px 22px', boxShadow: '0 1px 3px rgba(0,0,0,0.07)', marginBottom: 16 }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
              <div>
                <span style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: '#64748b' }}>Collection Progress</span>
                <span style={{ marginLeft: 10, fontSize: 12, color: '#94a3b8' }}>Paid vs. Total Due</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'baseline', gap: 6 }}>
                <span style={{ fontSize: 22, fontWeight: 700, color: barColor, lineHeight: 1 }}>{pct.toFixed(1)}%</span>
                <span style={{ fontSize: 11, color: '#94a3b8' }}>collected</span>
              </div>
            </div>
            <div style={{ height: 10, borderRadius: 99, background: '#f1f5f9', overflow: 'hidden' }}>
              <div style={{ height: '100%', width: `${pct}%`, borderRadius: 99, background: barColor, transition: 'width 0.4s ease' }} />
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 8 }}>
              <div style={{ fontSize: 11, color: '#64748b' }}>
                <span style={{ fontWeight: 600, color: barColor }}>{fmtShort(kpi?.totalPaidAmount)}</span> paid
              </div>
              <div style={{ fontSize: 11, color: '#64748b' }}>
                <span style={{ fontWeight: 600, color: '#dc2626' }}>{fmtShort(remaining > 0 ? remaining : 0)}</span> remaining
                &nbsp;·&nbsp; Total <span style={{ fontWeight: 600, color: '#0f172a' }}>{fmtShort(kpi?.totalDueAmount)}</span>
              </div>
            </div>
          </div>
        );
      })()}

      {/* ── Status Breakdown ─────────────────────────────── */}
      <div style={{ background: '#fff', borderRadius: 12, boxShadow: '0 1px 3px rgba(0,0,0,0.07)', padding: '14px 20px', display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center', marginBottom: 24 }}>
        <span style={{ fontSize: 11, color: '#64748b', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', marginRight: 4, whiteSpace: 'nowrap' }}>Status Breakdown</span>
        <span style={{
          display: 'inline-flex', alignItems: 'center', gap: 6,
          background: '#f1f5f9', borderRadius: 20, padding: '5px 14px',
        }}>
          <span style={{ fontSize: 14, fontWeight: 700, color: '#475569' }}>{kpi?.totalStudents ?? 0}</span>
          <span style={{ fontSize: 12, color: '#475569', fontWeight: 500 }}>All</span>
        </span>
        {STATUS_ORDER.map((s) => {
          const sc    = STATUS_COLORS[s] || { bg: '#f1f5f9', color: '#475569' };
          const count = statusCounts[s] || 0;
          return (
            <span
              key={s}
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 6,
                background: sc.bg, borderRadius: 20, padding: '5px 14px',
              }}
            >
              <span style={{ width: 7, height: 7, borderRadius: '50%', background: sc.color, display: 'inline-block', flexShrink: 0 }} />
              <span style={{ fontSize: 14, fontWeight: 700, color: sc.color }}>{count}</span>
              <span style={{ fontSize: 12, color: sc.color, textTransform: 'capitalize', fontWeight: 500 }}>
                {s.charAt(0).toUpperCase() + s.slice(1)}
              </span>
            </span>
          );
        })}
      </div>

      {/* ── Day-by-day Paid Amount bar chart ─────────────── */}
      <div style={{ background: '#fff', borderRadius: 12, padding: '20px 24px', boxShadow: '0 1px 3px rgba(0,0,0,0.07)', marginBottom: 16, borderTop: '3px solid #16a34a' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
          <div>
            <h2 style={{ fontSize: 15, fontWeight: 600, color: '#0f172a', margin: 0 }}>
              Daily Paid Amount
            </h2>
            <p style={{ fontSize: 12, color: '#94a3b8', margin: '4px 0 0' }}>
              {new Date().toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
            </p>
          </div>
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, background: '#dcfce7', borderRadius: 20, padding: '5px 14px' }}>
            <span style={{ width: 8, height: 8, borderRadius: '50%', background: '#16a34a', display: 'inline-block' }} />
            <span style={{ fontSize: 13, fontWeight: 700, color: '#166534' }}>Paid</span>
          </span>
        </div>
        <ResponsiveContainer width="100%" height={240}>
          <BarChart data={dailyPaid} margin={{ top: 4, right: 8, left: 8, bottom: 4 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
            <XAxis
              dataKey="label"
              tick={{ fontSize: 10, fill: '#94a3b8' }}
              axisLine={false}
              tickLine={false}
              interval={0}
            />
            <YAxis
              tickFormatter={(v) => `${fmtShort(v)}`}
              tick={{ fontSize: 11, fill: '#94a3b8' }}
              axisLine={false}
              tickLine={false}
              width={54}
            />
            <Tooltip
              content={<ChartTooltip />}
              cursor={{ fill: 'rgba(22,163,74,0.06)' }}
            />
            <Bar dataKey="amount" fill="#16a34a" radius={[4, 4, 0, 0]} maxBarSize={28} />
          </BarChart>
        </ResponsiveContainer>
        {(() => {
          const totalPaid = dailyPaid.reduce((sum, d) => sum + (d.amount ?? 0), 0);
          const peakDay = dailyPaid.reduce((best, d) => (!best || (d.amount ?? 0) > (best.amount ?? 0) ? d : best), null);
          return (
            <div style={{ display: 'flex', gap: 24, marginTop: 14, paddingTop: 14, borderTop: '1px solid #f0fdf4', flexWrap: 'wrap' }}>
              <div>
                <p style={{ fontSize: 11, color: '#94a3b8', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em', margin: '0 0 4px' }}>Month Total</p>
                <p style={{ fontSize: 20, fontWeight: 700, color: '#16a34a', margin: 0, lineHeight: 1 }}>Rs {fmt(totalPaid)}</p>
              </div>
              {peakDay?.amount > 0 && (
                <div>
                  <p style={{ fontSize: 11, color: '#94a3b8', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em', margin: '0 0 4px' }}>Peak Day</p>
                  <p style={{ fontSize: 20, fontWeight: 700, color: '#166534', margin: 0, lineHeight: 1 }}>Day {peakDay.label} &nbsp;<span style={{ fontSize: 13, color: '#16a34a', fontWeight: 500 }}>Rs {fmtShort(peakDay.amount)}</span></p>
                </div>
              )}
            </div>
          );
        })()}
      </div>

      {/* ── Day-by-day Confirmed Amount bar chart ────────── */}
      <div style={{ background: '#fff', borderRadius: 12, padding: '20px 24px', boxShadow: '0 1px 3px rgba(0,0,0,0.07)', marginBottom: 24, borderTop: '3px solid #7c3aed' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
          <div>
            <h2 style={{ fontSize: 15, fontWeight: 600, color: '#0f172a', margin: 0 }}>
              Daily Confirmed Amount
            </h2>
            <p style={{ fontSize: 12, color: '#94a3b8', margin: '4px 0 0' }}>
              {new Date().toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
            </p>
          </div>
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, background: '#ede9fe', borderRadius: 20, padding: '5px 14px' }}>
            <span style={{ width: 8, height: 8, borderRadius: '50%', background: '#7c3aed', display: 'inline-block' }} />
            <span style={{ fontSize: 13, fontWeight: 700, color: '#5b21b6' }}>Confirmed</span>
          </span>
        </div>
        <ResponsiveContainer width="100%" height={240}>
          <BarChart data={dailyConfirmed} margin={{ top: 4, right: 8, left: 8, bottom: 4 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#f5f3ff" vertical={false} />
            <XAxis
              dataKey="label"
              tick={{ fontSize: 10, fill: '#94a3b8' }}
              axisLine={false}
              tickLine={false}
              interval={0}
            />
            <YAxis
              tickFormatter={(v) => `${fmtShort(v)}`}
              tick={{ fontSize: 11, fill: '#94a3b8' }}
              axisLine={false}
              tickLine={false}
              width={54}
            />
            <Tooltip
              content={({ active, payload, label }) => {
                if (!active || !payload?.length || payload[0].value === null) return null;
                return (
                  <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 8, padding: '8px 14px', fontSize: 13 }}>
                    <p style={{ color: '#64748b', marginBottom: 4 }}>{label}</p>
                    <p style={{ color: '#7c3aed', fontWeight: 700 }}>Rs {fmt(payload[0].value)}</p>
                  </div>
                );
              }}
              cursor={{ fill: 'rgba(124,58,237,0.06)' }}
            />
            <Bar dataKey="amount" fill="#7c3aed" radius={[4, 4, 0, 0]} maxBarSize={28} />
          </BarChart>
        </ResponsiveContainer>
        {(() => {
          const totalConfirmed = dailyConfirmed.reduce((sum, d) => sum + (d.amount ?? 0), 0);
          const peakDay = dailyConfirmed.reduce((best, d) => (!best || (d.amount ?? 0) > (best.amount ?? 0) ? d : best), null);
          return (
            <div style={{ display: 'flex', gap: 24, marginTop: 14, paddingTop: 14, borderTop: '1px solid #f5f3ff', flexWrap: 'wrap' }}>
              <div>
                <p style={{ fontSize: 11, color: '#94a3b8', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em', margin: '0 0 4px' }}>Month Total</p>
                <p style={{ fontSize: 20, fontWeight: 700, color: '#7c3aed', margin: 0, lineHeight: 1 }}>Rs {fmt(totalConfirmed)}</p>
              </div>
              {peakDay?.amount > 0 && (
                <div>
                  <p style={{ fontSize: 11, color: '#94a3b8', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em', margin: '0 0 4px' }}>Peak Day</p>
                  <p style={{ fontSize: 20, fontWeight: 700, color: '#5b21b6', margin: 0, lineHeight: 1 }}>Day {peakDay.label} &nbsp;<span style={{ fontSize: 13, color: '#7c3aed', fontWeight: 500 }}>Rs {fmtShort(peakDay.amount)}</span></p>
                </div>
              )}
            </div>
          );
        })()}
      </div>

      {/* ── Extra Due Records Modal ───────────────────────── */}
      {extraModal.open && (() => {
        const paid      = extraModal.rows.filter((r) => r.dueStatus === 'paid');
        const confirmed = extraModal.rows.filter((r) => r.dueStatus === 'confirmed');
        const notPaid   = extraModal.rows.filter((r) => r.dueStatus !== 'paid');
        const totalAmt      = extraModal.rows.reduce((s, r) => s + r.totalDue, 0);
        const paidAmt       = paid.reduce((s, r) => s + (r.confirmedAmount ?? 0), 0);
        const confirmedAmt  = confirmed.reduce((s, r) => s + (r.confirmedAmount ?? 0), 0);
        const notPaidAmt    = notPaid.reduce((s, r) => s + r.totalDue, 0);

        const monthLabel = extraModal.month
          ? new Date(extraModal.month + '-01').toLocaleDateString('en-US', { month: 'long', year: 'numeric' })
          : '';

        const RowCard = ({ r }) => {
          const sc = STATUS_COLORS[r.dueStatus] || { bg: '#f1f5f9', color: '#475569' };
          return (
            <div style={{
              display: 'flex', alignItems: 'center', gap: 12, padding: '10px 14px',
              borderRadius: 8, background: '#fafafa', borderBottom: '1px solid #f1f5f9',
              flexWrap: 'wrap',
            }}>
              {/* Reg ID chip */}
              <span style={{
                flexShrink: 0, background: '#f1f5f9', borderRadius: 6,
                padding: '3px 10px', fontSize: 11, fontWeight: 700, color: '#475569',
                minWidth: 80, textAlign: 'center',
              }}>
                {r.regId || '—'}
              </span>
              {/* Student info */}
              <div style={{ flex: 1, minWidth: 140 }}>
                <p style={{ margin: 0, fontSize: 13, fontWeight: 600, color: '#0f172a', lineHeight: 1.3 }}>
                  {r.student || 'Unknown'}
                </p>
                <p style={{ margin: '2px 0 0', fontSize: 11, color: '#94a3b8' }}>
                  {[r.batch, r.course].filter(Boolean).join(' · ')}
                </p>
              </div>
              {/* Missing-field pills */}
              <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', flexShrink: 0 }}>
                {r.missingFields.map((f) => (
                  <span key={f} style={{
                    background: '#fef3c7', color: '#b45309', borderRadius: 20,
                    padding: '2px 8px', fontSize: 10, fontWeight: 600,
                  }}>{f}</span>
                ))}
              </div>
              {/* Amount */}
              <div style={{ textAlign: 'right', flexShrink: 0 }}>
                <p style={{ margin: 0, fontSize: 13, fontWeight: 700, color: '#0f172a' }}>Rs {fmt(r.totalDue)}</p>
                {r.confirmedAmount != null && (
                  <p style={{ margin: '2px 0 0', fontSize: 11, color: '#16a34a' }}>
                    Confirmed Rs {fmt(r.confirmedAmount)}
                  </p>
                )}
                {r.confirmDateToPay && (
                  <p style={{ margin: '1px 0 0', fontSize: 10, color: '#94a3b8' }}>{r.confirmDateToPay}</p>
                )}
              </div>
              {/* Status badge */}
              <span style={{
                flexShrink: 0, background: sc.bg, color: sc.color,
                borderRadius: 20, padding: '4px 12px', fontSize: 11, fontWeight: 700,
                textTransform: 'capitalize',
              }}>
                {r.dueStatus}
              </span>
            </div>
          );
        };

        return (
          <div
            onClick={() => setExtraModal({ open: false, loading: false, rows: [], month: '' })}
            style={{
              position: 'fixed', inset: 0, zIndex: 9000,
              background: 'rgba(15,23,42,0.55)', backdropFilter: 'blur(3px)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              padding: 16,
            }}
          >
            <div
              onClick={(e) => e.stopPropagation()}
              style={{
                background: '#fff', borderRadius: 16, width: '100%', maxWidth: 860,
                maxHeight: '90vh', display: 'flex', flexDirection: 'column',
                boxShadow: '0 20px 60px rgba(0,0,0,0.25)',
                overflow: 'hidden',
              }}
            >
              {/* Header */}
              <div style={{
                padding: '18px 24px 14px', borderBottom: '1px solid #f1f5f9',
                background: 'linear-gradient(135deg,#fffbeb 0%,#fff 100%)',
              }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
                  <div>
                    <h2 style={{ margin: 0, fontSize: 17, fontWeight: 700, color: '#0f172a' }}>
                      Extra Due Records
                    </h2>
                    <p style={{ margin: '3px 0 0', fontSize: 12, color: '#94a3b8' }}>{monthLabel}</p>
                  </div>
                  <button
                    onClick={() => setExtraModal({ open: false, loading: false, rows: [], month: '' })}
                    style={{
                      background: '#f1f5f9', border: 'none', borderRadius: 8, cursor: 'pointer',
                      width: 32, height: 32, fontSize: 17, color: '#64748b', display: 'flex',
                      alignItems: 'center', justifyContent: 'center', fontWeight: 700,
                    }}
                  >×</button>
                </div>
                {/* KPI pills */}
                <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                  {[
                    { label: 'Total Records', value: extraModal.rows.length, color: '#d97706', bg: '#fef3c7' },
                    { label: 'Not Paid', value: notPaid.length, color: '#dc2626', bg: '#fee2e2' },
                    { label: 'Confirmed', value: confirmed.length, color: '#7c3aed', bg: '#ede9fe' },
                    { label: 'Paid', value: paid.length, color: '#16a34a', bg: '#dcfce7' },
                    { label: 'Total Due', value: `Rs ${fmtShort(totalAmt)}`, color: '#b45309', bg: '#fef3c7' },
                    { label: 'Confirmed Due', value: `Rs ${fmtShort(confirmedAmt)}`, color: '#7c3aed', bg: '#ede9fe' },
                    { label: 'Collected', value: `Rs ${fmtShort(paidAmt)}`, color: '#16a34a', bg: '#dcfce7' },
                    { label: 'Outstanding', value: `Rs ${fmtShort(notPaidAmt)}`, color: '#dc2626', bg: '#fee2e2' },
                  ].map(({ label, value, color, bg }) => (
                    <span key={label} style={{
                      background: bg, borderRadius: 20, padding: '5px 14px',
                      fontSize: 12, fontWeight: 700, color,
                    }}>
                      {value} <span style={{ fontWeight: 400, opacity: 0.75 }}>{label}</span>
                    </span>
                  ))}
                </div>
              </div>

              {/* Body */}
              <div style={{ overflowY: 'auto', flex: 1, padding: '16px 24px' }}>
                {extraModal.loading ? (
                  <div style={{ textAlign: 'center', padding: '48px 0', color: '#94a3b8', fontSize: 14 }}>
                    Loading records…
                  </div>
                ) : extraModal.rows.length === 0 ? (
                  <div style={{ textAlign: 'center', padding: '48px 0', color: '#94a3b8', fontSize: 14 }}>
                    No extra due records found for this month.
                  </div>
                ) : (
                  <>
                    {/* Not Paid section */}
                    {notPaid.length > 0 && (
                      <div style={{ marginBottom: 24 }}>
                        <div style={{
                          display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10,
                          paddingBottom: 8, borderBottom: '2px solid #fee2e2',
                        }}>
                          <span style={{
                            background: '#dc2626', color: '#fff', borderRadius: 6,
                            padding: '3px 10px', fontSize: 11, fontWeight: 700,
                          }}>Not Paid</span>
                          <span style={{ fontSize: 12, color: '#94a3b8' }}>
                            {notPaid.length} record{notPaid.length !== 1 ? 's' : ''} · Outstanding Rs {fmt(notPaidAmt)}
                          </span>
                        </div>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                          {notPaid.map((r) => <RowCard key={r.dueId} r={r} />)}
                        </div>
                      </div>
                    )}

                    {/* Paid section */}
                    {paid.length > 0 && (
                      <div>
                        <div style={{
                          display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10,
                          paddingBottom: 8, borderBottom: '2px solid #dcfce7',
                        }}>
                          <span style={{
                            background: '#16a34a', color: '#fff', borderRadius: 6,
                            padding: '3px 10px', fontSize: 11, fontWeight: 700,
                          }}>Paid</span>
                          <span style={{ fontSize: 12, color: '#94a3b8' }}>
                            {paid.length} record{paid.length !== 1 ? 's' : ''} · Collected Rs {fmt(paidAmt)}
                          </span>
                        </div>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                          {paid.map((r) => <RowCard key={r.dueId} r={r} />)}
                        </div>
                      </div>
                    )}
                  </>
                )}
              </div>

              {/* Footer */}
              {!extraModal.loading && extraModal.rows.length > 0 && (
                <div style={{
                  padding: '12px 24px', borderTop: '1px solid #f1f5f9',
                  display: 'flex', gap: 16, flexWrap: 'wrap', alignItems: 'center',
                  background: '#f8fafc',
                }}>
                  <span style={{ fontSize: 12, color: '#64748b', flex: 1 }}>
                    <span style={{ fontWeight: 700, color: '#d97706' }}>{extraModal.rows.length}</span> total records ·&nbsp;
                    <span style={{ fontWeight: 700, color: '#dc2626' }}>{notPaid.length}</span> not paid ·&nbsp;
                    <span style={{ fontWeight: 700, color: '#16a34a' }}>{paid.length}</span> paid
                  </span>
                  <span style={{ fontSize: 12, color: '#64748b' }}>
                    Collected <span style={{ fontWeight: 700, color: '#16a34a' }}>Rs {fmt(paidAmt)}</span> of <span style={{ fontWeight: 700 }}>Rs {fmt(totalAmt)}</span>
                  </span>
                </div>
              )}
            </div>
          </div>
        );
      })()}

    </RecoveryLayout>
  );
}
